import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useAuth } from '@/contexts/auth-context';
import { apiClient } from '@/lib/api-client';
import { appEvents } from '@/lib/app-events';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { InlineAuthForm } from '@/components/auth/InlineAuthForm';

export default function InvitePage() {
	const { token } = useParams<{ token: string }>();
	const navigate = useNavigate();
	const { isAuthenticated, isLoading: authLoading } = useAuth();

	const [inviteInfo, setInviteInfo] = useState<{ orgName: string; valid: boolean } | null>(null);
	const [loading, setLoading] = useState(true);
	const [accepting, setAccepting] = useState(false);
	const [acceptError, setAcceptError] = useState<string | null>(null);
	const acceptAttempted = useRef(false);

	useEffect(() => {
		if (!token) return;
		let cancelled = false;
		apiClient.getInviteInfo(token).then(res => {
			if (cancelled) return;
			if (res.success && res.data) {
				setInviteInfo({ orgName: res.data.orgName, valid: res.data.valid });
			} else {
				setInviteInfo({ orgName: '', valid: false });
			}
			setLoading(false);
		}).catch(() => {
			if (cancelled) return;
			setInviteInfo({ orgName: '', valid: false });
			setLoading(false);
		});
		return () => { cancelled = true; };
	}, [token]);

	// Auto-accept when authenticated and invite is valid
	useEffect(() => {
		if (!token || !isAuthenticated || !inviteInfo?.valid || acceptAttempted.current) return;
		acceptAttempted.current = true;
		let cancelled = false;
		setAccepting(true);
		setAcceptError(null);

		apiClient.acceptInvite(token).then(res => {
			if (cancelled) return;
			if (res.success && res.data) {
				toast.success(`Joined ${res.data.orgName || 'organization'}`);
				// Org membership changed -- refresh the app list so org apps appear in the sidebar
				appEvents.emitAppsInvalidated();
				navigate('/settings');
			} else {
				setAccepting(false);
				setAcceptError(res.error?.message || 'Failed to accept invite');
				acceptAttempted.current = false;
			}
		}).catch(() => {
			if (cancelled) return;
			setAccepting(false);
			setAcceptError('Failed to accept invite');
			acceptAttempted.current = false;
		});

		return () => { cancelled = true; };
	}, [token, isAuthenticated, inviteInfo?.valid, navigate]);

	const handleRetry = async () => {
		if (!token) return;
		setAccepting(true);
		setAcceptError(null);
		try {
			const res = await apiClient.acceptInvite(token);
			if (res.success && res.data) {
				toast.success(`Joined ${res.data.orgName || 'organization'}`);
				appEvents.emitAppsInvalidated();
				navigate('/settings');
			} else {
				setAccepting(false);
				setAcceptError(res.error?.message || 'Failed to accept invite');
			}
		} catch {
			setAccepting(false);
			setAcceptError('Failed to accept invite');
		}
	};

	if (loading || authLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-bg-3">
				<div className="space-y-4 text-center">
					<Skeleton className="h-12 w-48 mx-auto" />
					<Skeleton className="h-4 w-32 mx-auto" />
				</div>
			</div>
		);
	}

	if (!inviteInfo?.valid) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-bg-3">
				<Card className="w-full max-w-md">
					<CardHeader>
						<CardTitle>Invalid Invite</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-sm text-muted-foreground">
							This invite link is invalid, expired, or has reached its maximum uses.
						</p>
						<Button variant="outline" onClick={() => navigate('/')}>
							Go Home
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Authenticated: auto-accept in progress or error with retry
	if (isAuthenticated) {
		if (acceptError) {
			return (
				<div className="min-h-screen flex items-center justify-center bg-bg-3">
					<Card className="w-full max-w-md">
						<CardHeader>
							<CardTitle>Join {inviteInfo.orgName}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<p className="text-sm text-muted-foreground">
								{acceptError || 'Something went wrong while joining. Please try again.'}
							</p>
							<Button onClick={handleRetry} disabled={accepting}>
								{accepting ? 'Joining...' : 'Try Again'}
							</Button>
						</CardContent>
					</Card>
				</div>
			);
		}

		return (
			<div className="min-h-screen flex items-center justify-center bg-bg-3">
				<Card className="w-full max-w-md">
					<CardHeader>
						<CardTitle>Joining {inviteInfo.orgName}</CardTitle>
					</CardHeader>
					<CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
						<Loader2 className="h-4 w-4 animate-spin" />
						<span>Joining organization...</span>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Unauthenticated: show invite info with inline auth form
	return (
		<div className="min-h-screen flex items-center justify-center bg-bg-3">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle>Join {inviteInfo.orgName}</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-sm text-muted-foreground">
						You've been invited to join <strong>{inviteInfo.orgName}</strong> on Hatchd.
						Sign in or create an account to continue.
					</p>
					<InlineAuthForm
						defaultMode="register"
						intendedUrl={`/invite/${token}`}
					/>
				</CardContent>
			</Card>
		</div>
	);
}
